import type { JsonValue } from './json.types'

/**
 * Tenant and Subscription Types
 * Matches: security.tenants, security.subscriptions, plan_limits tables
 */

// ============================================================================
// TENANT TYPES
// ============================================================================

/**
 * Tenant Type - Describes the organization persona; the subscription controls capacity
 * - solo_ca: Individual practitioner (Starter supports up to 3 active users)
 * - medium_firm: Small to mid-size firm (Professional supports up to 15 active users)
 * - large_firm: Enterprise firm (capacity comes from its server-side entitlement)
 */
export type TenantType = 'solo_ca' | 'medium_firm' | 'large_firm'

/**
 * Tenant Status
 */
export type TenantStatus = 'active' | 'suspended' | 'deleted' | 'pending'

/**
 * Address structure for firm details
 */
export interface FirmAddress {
  line1: string
  line2?: string
  city: string
  state: string
  pincode: string
  country: string
}

/**
 * Tenant (CA Firm)
 * Matches: security.tenants table
 */
export interface Tenant {
  tenantId: string
  tenantCode: string // Format: TH-Tenant-XXX
  tenantName: string
  tenantType: TenantType
  status: TenantStatus

  // Database config (display only)
  dbHost: string
  dbPort: number
  dbName: string
  dbUser?: string

  // Object storage
  objectBucket: string

  // Firm details
  firmGstin?: string
  firmPan?: string
  firmEmail?: string
  firmPhone?: string
  firmWebsite?: string
  firmAddress?: FirmAddress | JsonValue
  logoUrl?: string

  // Settings & metadata
  settings: TenantSettings | JsonValue
  metadata: JsonValue

  // Timestamps
  createdAt: string | null
  updatedAt: string | null
  deletedAt?: string
}

/**
 * Tenant Settings
 */
export interface TenantSettings {
  dateFormat?: string
  financialYearStart?: string // MM-DD format
  timezone?: string
  currency?: string
  defaultLanguage?: string
  firmWebsite?: string
  establishedYear?: string
  registrationNumber?: string
  notificationPreferences?: {
    email: boolean
    sms: boolean
  }
}

/**
 * Input for creating a tenant
 */
export interface CreateTenantInput {
  tenantName: string
  tenantType: TenantType
  firmEmail: string
  firmPhone?: string
  firmGstin?: string
  firmPan?: string
  firmAddress?: FirmAddress
}

/**
 * Input for updating a tenant
 */
export interface UpdateTenantInput {
  tenantName?: string
  firmEmail?: string
  firmPhone?: string
  firmWebsite?: string
  firmGstin?: string
  firmPan?: string
  firmAddress?: Partial<FirmAddress>
  logoUrl?: string
  settings?: Partial<TenantSettings>
}

// ============================================================================
// SUBSCRIPTION TYPES
// ============================================================================

/**
 * Subscription Plan Codes
 */
export type PlanCode = 'trial' | 'starter' | 'professional' | 'enterprise'

/**
 * Subscription Status
 */
export type SubscriptionStatus =
  | 'pending'
  | 'active'
  | 'trial'
  | 'grace'
  | 'suspended'
  | 'cancelled'
  | 'expired'

/**
 * Billing Cycle
 */
export type BillingCycle = 'monthly' | 'yearly'

/**
 * Subscription
 * Matches: security.subscriptions table
 */
export interface Subscription {
  subscriptionId: string
  tenantId: string

  // Plan details
  planCode: PlanCode
  status: SubscriptionStatus
  billingCycle: BillingCycle | null

  // Dates
  startsAt: string | null
  expiresAt?: string
  cancelledAt?: string
  graceEndsAt?: string
  suspendedAt?: string
  endedAt?: string
  cancelAtPeriodEnd?: boolean
  pendingPlanCode?: PlanCode
  pendingBillingCycle?: BillingCycle
  pendingChangeAt?: string
  pendingChangeStatus?: 'scheduled' | 'blocked_usage' | 'awaiting_payment' | 'payment_failed'
  pendingChangeDetails?: Record<string, unknown>

  // Custom limits (override plan defaults)
  maxUsers?: number
  maxClients?: number
  maxBranches?: number

  // Payment info (simplified for mock)
  paymentMethod?: {
    type: 'card' | 'upi' | 'netbanking'
    last4?: string
    brand?: string
  }
  lastPaymentAt?: string

  // Timestamps
  createdAt: string | null
  updatedAt: string | null
}

/**
 * Plan Limits (default limits per plan)
 * Matches: plan_limits table
 */
export interface PlanLimits {
  planCode: PlanCode
  planName: string
  description: string

  // Limits
  maxUsers: number
  maxClients: number
  maxBranches: number
  maxStorageGb: number

  // Features
  features: PlanFeatures

  // Pricing (INR)
  priceMonthly: number
  priceYearly: number

  isActive: boolean
}

/**
 * Plan Features
 */
export interface PlanFeatures {
  basicReports: boolean
  advancedReports: boolean
  bulkOperations: boolean
  apiAccess: boolean
  customBranding: boolean
  emailSupport: boolean
  prioritySupport: boolean
  dedicatedSupport: boolean
  sso: boolean
}

export interface EffectiveEntitlements {
  tenantId: string
  planCode: PlanCode
  planName: string
  subscriptionStatus: SubscriptionStatus
  limits: Record<string, number>
  features: Record<string, boolean>
  usage: Record<string, number>
  appliedOverrides: string[]
  upgrade: {
    upgradeAvailable: boolean
    recommendedPlan?: PlanCode
  }
}

export interface DowngradeBlocker {
  quotaKey: 'users' | 'clients' | 'branches' | 'storage_bytes'
  used: number
  targetLimit: number
  excess: number
  description: string
}

export interface SubscriptionInvoice {
  invoiceId: string
  invoiceNumber: string
  tenantId: string
  subscriptionId: string
  purpose: 'purchase' | 'renewal' | 'upgrade' | 'downgrade' | 'cycle_change' | 'reactivation'
  status: 'draft' | 'pending' | 'paid' | 'failed' | 'void' | 'refunded'
  requestedPlanCode: PlanCode
  requestedBillingCycle: BillingCycle
  amountMinor?: number
  taxMinor?: number
  totalMinor?: number
  currency?: string
  periodStart?: string
  periodEnd?: string
  dueAt?: string
  paidAt?: string
  providerPaymentId?: string
  receiptReference?: string
  failureReason?: string
  createdAt: string
  updatedAt: string
}

export interface SubscriptionLifecycleEvent {
  eventId: string
  tenantId: string
  subscriptionId: string
  invoiceId?: string
  eventType: string
  source: 'owner' | 'provider' | 'system'
  actorId?: string
  idempotencyKey: string
  previousStatus?: SubscriptionStatus
  newStatus?: SubscriptionStatus
  details: Record<string, unknown>
  createdAt: string
}

export interface BillingOverview {
  subscription: Subscription | null
  usage: Record<string, number>
  currentLimits: Record<string, number>
  downgradeBlockers: DowngradeBlocker[]
  invoices: SubscriptionInvoice[]
  history: SubscriptionLifecycleEvent[]
}

export interface SubscriptionCommandResult {
  subscription: Subscription
  invoice?: SubscriptionInvoice
  downgradeBlockers: DowngradeBlocker[]
  idempotentReplay: boolean
}

export interface PlanCommandInput {
  planCode: Exclude<PlanCode, 'trial'>
  billingCycle: BillingCycle
  idempotencyKey: string
}

// ============================================================================
// STATIC DATA
// ============================================================================

/**
 * Default plan limits
 */
/**
 * Get tenant type display name
 */
export function getTenantTypeDisplayName(type: TenantType): string {
  switch (type) {
    case 'solo_ca':
      return 'Solo Practitioner'
    case 'medium_firm':
      return 'Medium Firm'
    case 'large_firm':
      return 'Large Firm'
    default:
      return 'Unknown'
  }
}

/**
 * Get plan display name
 */
export function getPlanDisplayName(planCode: PlanCode): string {
  return planCode.charAt(0).toUpperCase() + planCode.slice(1)
}

/**
 * Get subscription status badge color
 */
export function getSubscriptionStatusColor(status: SubscriptionStatus): string {
  switch (status) {
    case 'active':
      return 'success'
    case 'trial':
      return 'info'
    case 'pending':
    case 'grace':
    case 'suspended':
      return 'warning'
    case 'cancelled':
    case 'expired':
      return 'error'
    default:
      return 'default'
  }
}

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * GSTIN format validation (Indian)
 * Format: 2 digits + 5 letters + 4 digits + 1 letter + 1 alphanumeric + Z + 1 alphanumeric
 */
export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/

/**
 * PAN format validation (Indian)
 * Format: 5 letters + 4 digits + 1 letter
 */
export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/

/**
 * Validate GSTIN
 */
export function isValidGstin(gstin: string): boolean {
  return GSTIN_REGEX.test(gstin)
}

/**
 * Validate PAN
 */
export function isValidPan(pan: string): boolean {
  return PAN_REGEX.test(pan)
}
